import { type ElementType, type ReactNode } from "react";
import {
  AccountTree,
  AccessTime,
  Add,
  AddComment,
  ArrowBack,
  ArrowDownward,
  ArrowUpward,
  BookmarkBorder,
  Business,
  Calculate,
  CalendarMonth,
  CameraAlt,
  ChatBubbleOutlined,
  Check,
  CheckCircle,
  CheckCircleOutlined,
  Checklist,
  Close,
  Comment,
  ContentCopy,
  DeleteOutlined,
  Description,
  Download,
  Edit,
  Email,
  ErrorOutlined,
  FilterList,
  FolderOpen,
  HelpOutlined,
  History,
  Home,
  Info,
  InfoOutlined,
  KeyboardArrowDown,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  KeyboardArrowUp,
  Lightbulb,
  LibraryBooks,
  LocalCafe,
  LocalOffer,
  LocationOn,
  Lock,
  MailOutlined,
  Menu,
  MoreHoriz,
  Newspaper,
  NoteAdd,
  NotificationsNone,
  OpenInNew,
  Person,
  Phone,
  Print,
  Refresh,
  Remove,
  RestartAlt,
  Save,
  Search,
  Settings,
  Share,
  ShoppingCart,
  Star,
  SwapVert,
  TrendingUp,
  ViewModule,
  Visibility,
  VisibilityOff,
  WarningAmber,
} from "@mui/icons-material";
import { Active as ActiveIcon } from "@schaeffler-commerce/design-system";
import { Assembly as AssemblyIcon } from "@schaeffler-commerce/design-system";
import { BallBearing as BallBearingIcon } from "@schaeffler-commerce/design-system";
import { Cad as CadIcon } from "@schaeffler-commerce/design-system";
import { DmcScan as DmcScanIcon } from "@schaeffler-commerce/design-system";
import { FileCheck as FileCheckIcon } from "@schaeffler-commerce/design-system";
import { FileClock as FileClockIcon } from "@schaeffler-commerce/design-system";
import { FileError as FileErrorIcon } from "@schaeffler-commerce/design-system";
import { FilePencil as FilePencilIcon } from "@schaeffler-commerce/design-system";
import { FlashlightOff as FlashlightOffIcon } from "@schaeffler-commerce/design-system";
import { FlashlightOn as FlashlightOnIcon } from "@schaeffler-commerce/design-system";
import { Pdf as PdfIcon } from "@schaeffler-commerce/design-system";
import { ScreenNews as ScreenNewsIcon } from "@schaeffler-commerce/design-system";
import type { CMSIconCode, CMSIconType } from "~/features/cms/types/icon";

export const CMS_ICON_MAP = {
  Active: ActiveIcon,
  ArrowDown: ArrowDownward,
  ArrowLeft: ArrowBack,
  ArrowUp: ArrowUpward,
  ArrowsUpDown: SwapVert,
  Assembly: AssemblyIcon,
  BallBearing: BallBearingIcon,
  Bell: NotificationsNone,
  Bin: DeleteOutlined,
  Bookmark: BookmarkBorder,
  Building: Business,
  Cad: CadIcon,
  Calculator: Calculate,
  Calendar: CalendarMonth,
  Camera: CameraAlt,
  Cart: ShoppingCart,
  CheckCircleOutline: CheckCircleOutlined,
  CheckCircle,
  Check,
  Checklist,
  ChevronDown: KeyboardArrowDown,
  ChevronLeft: KeyboardArrowLeft,
  ChevronRight: KeyboardArrowRight,
  ChevronUp: KeyboardArrowUp,
  Clock: AccessTime,
  Close,
  Cog: Settings,
  CommentEmpty: ChatBubbleOutlined,
  CommentPlus: AddComment,
  Comment,
  CopyToClipboard: ContentCopy,
  Cup: LocalCafe,
  Disk: Save,
  DmcScan: DmcScanIcon,
  Download,
  EnvelopeOpen: MailOutlined,
  Envelope: MailOutlined,
  ErrorCircle: ErrorOutlined,
  EyeOff: VisibilityOff,
  Eye: Visibility,
  FileCheck: FileCheckIcon,
  FileClock: FileClockIcon,
  FileError: FileErrorIcon,
  FileList: Description,
  FilePencil: FilePencilIcon,
  FilePlus: NoteAdd,
  Filter: FilterList,
  FlashlightOff: FlashlightOffIcon,
  FlashlightOn: FlashlightOnIcon,
  FolderOpen,
  History,
  Home,
  InfoCircleOutline: InfoOutlined,
  InformationCircle: Info,
  Invitation: Email,
  Lightbulb,
  List: Menu,
  Location: LocationOn,
  Lock,
  Minus: Remove,
  More: MoreHoriz,
  Newspaper,
  OpenInNew,
  Pages: LibraryBooks,
  Pdf: PdfIcon,
  Pencil: Edit,
  Phone,
  Plus: Add,
  PriceTag: LocalOffer,
  Printer: Print,
  QuestionMarkCircle: HelpOutlined,
  Reload: Refresh,
  Reset: RestartAlt,
  ScreenNews: ScreenNewsIcon,
  Search,
  Share,
  Star,
  Tiles: ViewModule,
  TreeView: AccountTree,
  Trending: TrendingUp,
  User: Person,
  Warning: WarningAmber,
} satisfies Record<CMSIconType, ElementType>;

export const resolveCMSIcon = (icon: CMSIconCode | null | undefined): ReactNode | undefined => {
  if (icon == null) {
    return undefined;
  }

  const Icon = Object.prototype.hasOwnProperty.call(CMS_ICON_MAP, icon) ? CMS_ICON_MAP[icon as CMSIconType] : undefined;

  if (!Icon) {
    console.warn(`[CMSColumnLinks] Unsupported CMS icon code "${icon}".`);
    return <HelpOutlined data-testid="cms-icon-fallback" data-cms-icon-code={icon} sx={{ fontSize: 24 }} />;
  }

  return <Icon data-cms-icon-code={icon} sx={{ fontSize: 24 }} />;
};
